import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { useAuthStore } from '@/stores/authStore'

export interface Notification {
  id: string
  type: string
  title: string
  body: string | null
  data: Record<string, string>
  readAt: string | null
  createdAt: string
}

export interface NotificationPage {
  items: Notification[]
  total: number
  unread: number
}

export const notificationsApi = {
  list: () => api.get<NotificationPage>('/notifications'),
  markRead: (id: string) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
}

/** Ερωτήματα που πρέπει να ανανεωθούν ανά τύπο ειδοποίησης. */
const INVALIDATE_ON: Record<string, string[][]> = {
  order_received: [['orders'], ['notifications']],
  order_confirmed: [['orders'], ['order'], ['notifications']],
  order_partially_confirmed: [['orders'], ['order'], ['notifications']],
  order_delivered: [['orders'], ['order'], ['notifications']],
  order_cancelled: [['orders'], ['order'], ['notifications']],
  connection_request: [['supplier-customers'], ['notifications']],
  supplier_claimed: [['suppliers'], ['supplier'], ['notifications']],
}

/**
 * Ζωντανές ειδοποιήσεις μέσω WebSocket, με invalidation του TanStack Query (§9).
 *
 * Δεν γίνεται περιοδικό polling. Μόνο όσο το WebSocket είναι κλειστό πέφτουμε
 * σε εφεδρικό interval 60 δευτερολέπτων, όπως ορίζει η προδιαγραφή.
 */
export function useNotifications() {
  const queryClient = useQueryClient()
  const token = useAuthStore((s) => s.session?.accessToken ?? null)
  const organizationId = useAuthStore((s) => s.session?.organizationId ?? null)
  const [connected, setConnected] = useState(false)
  const socketRef = useRef<WebSocket | null>(null)

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsApi.list,
    enabled: Boolean(token),
    refetchInterval: connected ? false : 60_000,
  })

  useEffect(() => {
    if (!token || !organizationId) return

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const socket = new WebSocket(
      `${protocol}://${window.location.host}/api/v1/ws/notifications?token=${encodeURIComponent(token)}`,
    )
    socketRef.current = socket

    // Ping κάθε 30 δευτερόλεπτα: κρατά τη σύνδεση ζωντανή μέσα από proxies
    // που κλείνουν αδρανείς συνδέσεις.
    let keepAlive: number | undefined

    socket.onopen = () => {
      setConnected(true)
      keepAlive = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send('ping')
      }, 30_000)
    }

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data as string)
        if (message.type !== 'notification') return

        const keys = INVALIDATE_ON[message.payload?.type] ?? [['notifications']]
        for (const key of keys) {
          void queryClient.invalidateQueries({ queryKey: key })
        }
      } catch {
        // Ένα κακοσχηματισμένο μήνυμα δεν πρέπει να ρίξει τη σύνδεση.
      }
    }

    socket.onclose = () => {
      setConnected(false)
      if (keepAlive) window.clearInterval(keepAlive)
    }

    return () => {
      if (keepAlive) window.clearInterval(keepAlive)
      socket.close()
      socketRef.current = null
    }
  }, [token, organizationId, queryClient])

  return {
    notifications: query.data?.items ?? [],
    unread: query.data?.unread ?? 0,
    isLoading: query.isPending,
    connected,
  }
}

/** Εγγραφή της συσκευής σε Web Push, αν ο χρήστης το επιτρέψει. */
export async function enablePush(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported'

  const config = await api.get<{ publicKey: string | null; enabled: boolean }>('/push/config')
  if (!config.enabled || !config.publicKey) return 'unsupported'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return 'denied'

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(config.publicKey),
  })

  await api.post('/push/subscribe', subscription.toJSON())
  return 'granted'
}

/** Ο VAPID κλειδί ταξιδεύει ως base64url και ο browser θέλει Uint8Array. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(normalised)
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)))
}
